import { FC, ReactNode } from "react";

import { Box, Stack } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { SocialNetworkLink, RegisteredCharityDetails } from "@simplyk/common";

import { Address } from "./Address/Address";
import { CustomBlock } from "./CustomBlock/CustomBlock";
import { SocialLinks } from "./SocialLinks/SocialLinks";

import { RegisteredCharityStatement } from "@/canvas/components/RegisteredCharityStatement/RegisteredCharityStatement";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { ExternalLink } from "@/icons/outlined";

const FOOTER_TEXT_TYPOGRAPHY_SX = { typography: { xs: "body2", sm: "body1" } };

const linkSx = (themeArg: Theme) => ({
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  minWidth: 0,
  color: "inherit",
  textDecoration: "none",
  "& svg": { visibility: "hidden" },
  "&:hover": {
    textDecoration: "underline",
    textDecorationColor: themeArg.palette.forms.text.intense,
    "& svg": { visibility: "visible" },
  },
});

export type OrganizationPageFooterBlock = { id: string; title: string; content: string };

type OrganizationPageFooterProps = {
  websiteUrl: string | null;
  contactUsButton?: ReactNode;
  socialLinks: SocialNetworkLink[];
  address: { label: string; mapsUrl: string } | null;
  footerBlocks: OrganizationPageFooterBlock[];
  registeredCharityDetails?: RegisteredCharityDetails | null;
  isPreview: boolean;
  theme: Theme;
  onWebsiteClick: () => void;
  onAddressClick: () => void;
  onAddressCopy: () => void;
};

export const OrganizationPageFooter: FC<OrganizationPageFooterProps> = ({
  websiteUrl,
  contactUsButton,
  socialLinks,
  address,
  footerBlocks,
  registeredCharityDetails,
  isPreview,
  theme,
  onWebsiteClick,
  onAddressClick,
  onAddressCopy,
}) => {
  const { t } = useTranslate();
  const labelSx = { ...FOOTER_TEXT_TYPOGRAPHY_SX, color: theme.palette.forms.text.moderate, opacity: 0.6 };
  const valueSx = { ...FOOTER_TEXT_TYPOGRAPHY_SX, color: theme.palette.forms.text.moderate };
  const hasActions = Boolean(contactUsButton) || socialLinks.length > 0;

  return (
    <Stack gap={{ xs: 3, sm: 2 }}>
      {(websiteUrl || hasActions) && (
        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          gap={{ xs: 3, sm: 2 }}
        >
          {websiteUrl && (
            <Stack sx={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
              <FormTypography sx={labelSx}>{t("organization", "ourWebsite")}</FormTypography>
              <Box
                {...(isPreview
                  ? { component: "span" as const }
                  : {
                      component: "a" as const,
                      href: websiteUrl,
                      target: "_blank",
                      rel: "noopener",
                      onClick: onWebsiteClick,
                    })}
                sx={isPreview ? { display: "flex", minWidth: 0 } : linkSx}
              >
                <FormTypography sx={{ ...valueSx, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {websiteUrl}
                </FormTypography>
                {!isPreview && (
                  <FormIcon size="small" sx={{ color: theme.palette.forms.text.moderate }}>
                    <ExternalLink />
                  </FormIcon>
                )}
              </Box>
            </Stack>
          )}
          {hasActions && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={1.5}
              sx={{ marginLeft: { xs: 0, sm: "auto" } }}
            >
              {contactUsButton}
              {socialLinks.length > 0 && <SocialLinks socialLinks={socialLinks} isPreview={isPreview} />}
            </Stack>
          )}
        </Stack>
      )}
      {address && (
        <Address
          label={address.label}
          mapsUrl={address.mapsUrl}
          isPreview={isPreview}
          textTypographySx={FOOTER_TEXT_TYPOGRAPHY_SX}
          labelSx={labelSx}
          onOpen={onAddressClick}
          onCopy={onAddressCopy}
        />
      )}
      {footerBlocks.map((footerBlock) => (
        <CustomBlock key={footerBlock.id} title={footerBlock.title} content={footerBlock.content} labelSx={labelSx} />
      ))}
      {registeredCharityDetails && (
        <Stack>
          <FormTypography sx={labelSx}>{t("organization", "legal")}</FormTypography>
          <Box sx={FOOTER_TEXT_TYPOGRAPHY_SX}>
            <RegisteredCharityStatement
              registeredName={registeredCharityDetails.registeredName}
              registrationNumber={registeredCharityDetails.registrationNumber}
              variant="inherit"
              vibe="moderate"
              align="inherit"
            />
          </Box>
        </Stack>
      )}
    </Stack>
  );
};
